/**
 * Décision 437 (TL, 31/10) — Pilotage → Suivi des DNS : en lecture seule,
 * pour la Direction, la chargée de clientèle et le Super administrateur.
 *
 * Aucune définition n'est neuve : chacune reprend celle du code qui la tient.
 * - « Active » : l'entreprise couvre ses salariés (`entrepriseCouvre`,
 *   `rules/eligibilite.ts`) — ni suspendue ni résiliée.
 * - « Avec un DNS en cours » : au moins un DNS complet appliqué qui fait foi
 *   (le dernier DNS de l'éligibilité, celui de la vue « DNS et salaires »).
 * - « En retard » : l'alerte des 60 jours (`conditionObsolete`).
 * - Un import « en échec » ou « à mapper » : son statut au module DNS, l'échec
 *   qu'un dépôt plus récent a remplacé ne comptant plus (`echecsRemplaces`).
 *
 * Jamais un salaire : des nombres d'entreprises et de personnes couvertes.
 */
import { z } from 'zod';
import { STATUTS_ADHERENT } from '../rules/eligibilite.js';
import { ORDRES_DE_TRI, schemaPage, schemaPagination } from './administration.js';
/** Ce qu'ouvre chaque tuile : la liste des entreprises concernées. */
export const CATEGORIES_SUIVI_DNS = [
    'ACTIVES',
    'AVEC_DNS',
    'SANS_DNS',
    'SANS_SALARIE',
    'RECUS_CE_MOIS',
    'IMPORT_EN_ECHEC',
    'IMPORT_A_MAPPER',
    'EN_RETARD',
    'SUSPENDUES',
    'RESILIEES',
];
export const schemaFiltresSuiviDns = z
    .object({
    /** Absent : toutes les entités. */
    entiteId: z.uuid().optional(),
})
    .strict();
const nombre = z.number().int().min(0);
export const schemaEntiteSuiviDns = z.object({
    entiteId: z.uuid(),
    code: z.string(),
    nom: z.string(),
    actives: nombre,
    salaries: nombre,
    ayantsDroit: nombre,
});
export const schemaSuiviDns = z.object({
    genereLe: z.iso.datetime(),
    /** Le premier jour du mois civil à Madagascar, `AAAA-MM-JJ`. */
    debutDuMois: z.string(),
    delaiAlerteJours: z.number().int(),
    entiteId: z.uuid().nullable(),
    /** Toutes les entités, pour le filtre — même celles sans entreprise. */
    entites: z.array(z.object({ id: z.uuid(), code: z.string(), nom: z.string() })),
    compteurs: z.object({
        /** Toutes les entreprises de la plateforme (non supprimées). */
        total: nombre,
        actives: nombre,
        suspendues: nombre,
        resiliees: nombre,
        /** Parmi les actives. */
        avecDns: nombre,
        sansDns: nombre,
        sansSalarie: nombre,
        recusCeMois: nombre,
        importEnEchec: nombre,
        importAMapper: nombre,
        enRetard: nombre,
        /** Couverts par le dernier DNS des entreprises actives. */
        salaries: nombre,
        ayantsDroit: nombre,
    }),
    /** Les entités qui ont au moins une entreprise, par code. */
    parEntite: z.array(schemaEntiteSuiviDns),
    /** Le temps de calcul côté serveur, en millisecondes. */
    dureeMs: nombre,
});
export const TRIS_SUIVI_DNS = ['nom', 'code', 'entite', 'statut', 'dernierDns', 'salaries'];
export const schemaFiltresEntreprisesSuiviDns = schemaPagination
    .extend({
    categorie: z.enum(CATEGORIES_SUIVI_DNS),
    entiteId: z.uuid().optional(),
    tri: z.enum(TRIS_SUIVI_DNS).default('nom'),
    ordre: z.enum(ORDRES_DE_TRI).default('asc'),
})
    .strict();
export const schemaEntrepriseSuiviDns = z.object({
    id: z.uuid(),
    code: z.string(),
    nom: z.string(),
    entiteCode: z.string(),
    statut: z.enum(STATUTS_ADHERENT),
    /** La réception du dernier DNS (CP-11), `null` : jamais reçu. */
    dernierDnsRecuLe: z.iso.datetime().nullable(),
    /** `null` : aucun DNS complet appliqué. */
    salaries: z.number().int().nullable(),
    ayantsDroit: z.number().int().nullable(),
});
export const schemaPageEntreprisesSuiviDns = schemaPage(schemaEntrepriseSuiviDns);
//# sourceMappingURL=suivi-dns.js.map